<script setup>
import {ref, onMounted, computed, onUnmounted, watch} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {gregorianDate} from "@/utils/gregorianDate.js";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import {formatPrice} from "@/utils/priceFormatter.js";
import Filters from "@/views/orders/Filters/index.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import {showMessage} from "@/utils/showMessage.js";
import {useOrderFilter} from "@/composables/useOrderFilter.js";
import {useRouter} from "vue-router";
import SortIcon from "@/components/datatable/sortIcon.vue";

const router = useRouter();
let interval = null;
const cleared = ref(false);
const search = computed(() => ordersStore.shopOrdersSearch);
const ordersStore = useMainOrdersStore();
const orders = computed(() => {
  return ordersStore.getOrders('shop').map(order => ({
    ...order,
    price: order.price || order.price === 0
        ? Number(order.price)
        : null,
    cost: order.cost || order.cost === 0
        ? Number(order.cost)
        : null,
    revenue: order.revenue || order.revenue === 0
        ? Number(order.revenue)
        : null,
    usd_tmn: order.usd_tmn || order.usd_tmn === 0
        ? Number(order.usd_tmn)
        : null,
    tomanPrice: order.tomanPrice || order.tomanPrice === 0
        ? Number(order.tomanPrice)
        : null,
    tomanCost: order.tomanCost || order.tomanCost === 0
        ? Number(order.tomanCost)
        : null,
    tomanRevenue: order.tomanRevenue || order.tomanRevenue === 0
        ? Number(order.tomanRevenue)
        : null,
  }))
});

const fetchNewOrders = async () => {
  if (cleared.value) return;

  if (orders.value.length > 0 && !ordersStore.isGettingNewOrders) {
    await ordersStore.action_getOrders('Shop');
  }

  if (!cleared.value) {
    interval = setTimeout(fetchNewOrders, 2000);
  }
};

async function applyFilters() {
  ordersStore.reset_lastUpdate();
  ordersStore.reset_orders('shopOrders');
  await ordersStore.action_getOrders('Shop');
}

function orderDetails({data}) {
  const route = {name: 'shop-order-details', params: {id: 0}}
  if (data.uuid) {
    route.params.id = data.uuid
  } else {
    route.params.id = data.id
  }
  router.push(route)
}

function onSortChange(params) {
  const sortOrder = params.sortOrder === 1 ? 'asc' : 'desc';
  ordersStore.setShopOrdersSort(params.sortField, sortOrder);
}

const {filtered: filteredOrders} = useOrderFilter(
    orders,
    search,
    'shop'
);

watch(filteredOrders, (newVal) => {
  ordersStore.shopFilteredOrders = newVal;
});

onMounted(() => {
  ordersStore.action_getOrders('Shop');
  fetchNewOrders();
})

onUnmounted(() => {
  clearTimeout(interval);
  cleared.value = true
});
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <AllButtons summaryPermission="ORDERS_SHOP_ACCESS_SUMMARY" addOrderPermission="ORDERS_SHOP_UPDATE"
                    addOrderRoute="shop-order-add" summary="ShopSummary" :applyFilter="applyFilters"
                    v-model="ordersStore.shopOrdersSearch" :copyButton="false"/>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable v-else
                 :nullSortOrder="-1"
                 :value="filteredOrders"
                 scrollable
                 scrollHeight="flex"
                 :virtualScrollerOptions="{ itemSize: 32 }"
                 resizableColumns
                 removableSort
                 stripedRows
                 class="text-xs w-full"
                 size="small"
                 tableStyle="width: 100%; table-layout: fixed;"
                 :sortField="ordersStore.shopOrdersSort?.field"
                 :sortOrder="ordersStore.shopOrdersSort?.type === 'asc' ? 1 : -1"
                 row-hover
                 @row-click="orderDetails"
                 @sort="onSortChange"
                 dataKey="id"
                 v-model:selection="ordersStore.selectedShopOrders"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column selectionMode="multiple" style="width: 36px;" headerStyle="width: 36px !important"></Column>
        <Column field="id" header="ID" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop class="w-full block h-full py-[0.375rem]">{{ slotProps.data.id }}</span>
          </template>
        </Column>
        <Column field="title" header="Title" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 12%; min-width: 180px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="customerName" header="Customer" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.customerName
                  ? slotProps.data.customerName
                  : slotProps.data.customer ? slotProps.data.customer : ''
            }}
          </template>
        </Column>
        <Column field="price" header="Price" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.price || slotProps.data.price === 0 ? formatPrice(slotProps.data.price) + ' $' : '' }}
          </template>
        </Column>
        <Column field="cost" header="Cost" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="!text-red-400">
              {{
                slotProps.data.cost || slotProps.data.cost === 0 ? formatPrice(slotProps.data.cost) + ' $' : ''
              }}
            </span>
          </template>
        </Column>
        <Column field="revenue" header="Revenue" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span
                :class="{ '!text-red-400': slotProps.data.revenue < 0, '!text-success-500': slotProps.data.revenue > 0 }">
              {{
                slotProps.data.revenue || slotProps.data.revenue === 0 ? formatPrice(slotProps.data.revenue) + ' $' : ''
              }}
            </span>
          </template>
        </Column>
        <Column field="usd_tmn" header="USD/TMN" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.usd_tmn || slotProps.data.usd_tmn === 0 ? formatPrice(slotProps.data.usd_tmn) : ''
            }}
          </template>
        </Column>
        <Column field="tomanPrice" header="Price" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.tomanPrice || slotProps.data.tomanPrice === 0 ? formatPrice(slotProps.data.tomanPrice, 0) + ' Tmn' : ''
            }}
          </template>
        </Column>
        <Column field="tomanCost" header="Cost" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="!text-red-400">
              {{
                slotProps.data.tomanCost || slotProps.data.tomanCost === 0 ? formatPrice(slotProps.data.tomanCost, 0) + ' Tmn' : ''
              }}
            </span>
          </template>
        </Column>
        <Column field="tomanRevenue" header="Revenue" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span
                :class="{ '!text-red-400': slotProps.data.tomanRevenue < 0, '!text-success-500': slotProps.data.tomanRevenue > 0 }">
              {{
                slotProps.data.tomanRevenue || slotProps.data.tomanRevenue === 0 ? formatPrice(slotProps.data.tomanRevenue, 0) + ' Tmn' : ''
              }}
            </span>
          </template>
        </Column>
        <Column field="state" header="Status" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <StateColumn :state="slotProps.data.state"/>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
            </span>
          </template>
        </Column>
        <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
    </div>
    <Filters v-if="ordersStore.isShowFilterModal" @applyFilter="applyFilters" :filters="['states']"/>
  </div>
</template>